<template>
  <div class="justify-space-between d-flex pa-4 tr-head">
    <div style="width: 33%" class="font-weight-medium ml-14">Make</div>
    <div style="width: 33%" class="font-weight-medium">Model</div>
    <div style="width: 33%" class="font-weight-medium">Trim</div>
  </div>
  <draggable
    v-model="vehiclesStore.vehiclesData"
    v-if="showDraggable"
    @start="handleDragStart"
    @touchstart="handleDragStart"
    @touchend="handleDragEnd"
    @end="handleDragEnd"
    item-key="model"
    v-bind="{ handle: '.drag-handle', forceFallback: true }"
  >
    <template #item="{ element }">
      <div>
        <v-dialog max-width="500" v-model="showEditDialog[element.model]">
          <template v-slot:activator="{ props: activatorProps }">
            <div
              class="d-flex pa-4 tr-border justify-space-between align-center"
              style="width: 100%"
              v-bind="activatorProps"
            >
              <v-btn icon="mdi-drag" size="small" class="mr-4 drag-handle"></v-btn>
              <div style="width: 33%" class="text-truncate">
                {{ element.make }}
              </div>
              <div style="width: 33%" class="text-truncate">
                {{ element.model }}
              </div>
              <div style="width: 33%" class="text-truncate">
                {{ element.trim }}
              </div>
            </div>
          </template>
          <template v-slot:default="{ isActive }">
            <v-card
              min-height="400px"
              class="d-flex flex-column justify-space-between"
            >
              <v-card-title class="text-center pt-4 text-whitespace"
                >{{ element.make }} {{ element.model ? element.model : '' }}
                {{ element.trim ? element.trim : '' }}
              </v-card-title>

              <!-- CUSTOM COUNT TEXT -->
              <CustomCountText
                :selected-placement="props.selectedPlacement"
                :make="element.make"
                :model="element.model"
                :disable-input="disableUrlAndCustomCountText"
              />
              <!-- SHOW/HIDE IF COUNT IS ZERO -->
              <ToggleIfCountIsZero
                :selected-placement="props.selectedPlacement"
                :make="element.make"
                :model="element.model"
                @hide-on-zero-stock="
                  data => (disableUrlAndCustomCountText = !data)
                "
              />
              <!-- CUSTOM URL FOR THE JELLYBEAN -->
              <CustomUrl
                :selected-placement="props.selectedPlacement"
                :make="element.make"
                :model="element.model"
                :disable-input="disableUrlAndCustomCountText"
              />
              <!-- DISABLE CUSTOM URL WHEN COUNt ZERO -->
              <DisableUrlIfCountZero
                :selected-placement="props.selectedPlacement"
                :make="element.make"
                :model="element.model"
              />
              <!-- CUSTOM IMAGE FIELD -->
              <CustomImage
                :selected-placement="props.selectedPlacement"
                :make="element.make"
                :model="element.model"
              />
              <!-- delete/save buttons -->
              <div class="d-flex justify-center pb-4">
                <v-btn
                  color="error"
                  @click="showDeleteDialog[element.model] = true"
                  class="ml-sm-2"
                >
                  <v-icon>mdi-delete</v-icon>
                  <span class="d-none d-sm-block">Delete</span>
                </v-btn>
                <v-btn
                  color="primary"
                  class="ml-sm-2"
                  @click="
                    savePlacementSettings();
                    showEditDialog[element.model] = false
                  "
                >
                  <v-icon>mdi-content-save</v-icon>
                  <span class="d-none d-sm-block">Save</span>
                </v-btn>
              </div>
            </v-card>
          </template>
        </v-dialog>

        <!-- Delete Placement Dialog -->
        <v-dialog v-model="showDeleteDialog[element.model]" max-width="500px">
          <v-card>
            <v-card-title>Delete Record From Preview</v-card-title>
            <v-card-text>
              Are you sure you want to delete the record ?
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                variant="text"
                @click="showDeleteDialog[element.model] = false"
              >
                Cancel
              </v-btn>
              <v-btn
                color="error"
                @click="
                  deleteRecordFromFilters(element.make, element.model);
                  showEditDialog[element.model] = false
                "
              >
                Delete
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>
    </template>
  </draggable>
  <div v-else>
    <v-skeleton-loader type="table"></v-skeleton-loader>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useVehiclesStore } from '@/stores/vehicles'
import { useSettingsStore } from '@/stores/settings'
import CustomCountText from './jellybean-edit/CustomCountText.vue'
import CustomUrl from './jellybean-edit/CustomUrl.vue'
import DisableUrlIfCountZero from './jellybean-edit/DisableUrlIfCountZero.vue'
import CustomImage from './jellybean-edit/CustomImage.vue'
import ToggleIfCountIsZero from './jellybean-edit/ToggleIfCountIsZero.vue'
import draggable from 'vuedraggable'

const props = defineProps<{
  selectedPlacement: string
}>()

const settingsStore = useSettingsStore()

const showDraggable = ref(true)
const drag = ref(false)

const showDeleteDialog = ref<Record<string, boolean>>({})
const showEditDialog = ref<Record<string, boolean>>({})
const disableUrlAndCustomCountText = ref(false)

const vehiclesStore = useVehiclesStore()

const savePlacementSettings = async () => {
  if (props.selectedPlacement === null) return
  await settingsStore
    .updateSettings(settingsStore.placements)
    .then(() => {})
    .catch((err: any) => {})
}

const handleDragStart = () => {
  drag.value = true

  // record that user has reordered items by drag and default sorting should be removed.
  settingsStore.placements.placements[props.selectedPlacement].usedDrag = true
}

const handleDragEnd = () => {
  // force re-render of the draggable, to avoid a bug of duplicating items on the table
  // showDraggable.value = false
  // setTimeout(() => {
  //   showDraggable.value = true
  // }, 200)

  settingsStore.placements.placements[props.selectedPlacement].filter =
    vehiclesStore.vehiclesData
    console.log(settingsStore.placements.placements[props.selectedPlacement].filter)
}

const deleteRecordFromFilters = (make: string, model: string) => {
  const itemToDelete = vehiclesStore.vehiclesData.findIndex(
    item => item.make === make && item.model === model,
  )
  vehiclesStore.vehiclesData.splice(itemToDelete, 1)
  showDeleteDialog.value[model] = false
}
</script>

<style>
.tr-hover:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.tr-border,
.tr-head {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  cursor: pointer;
}

.text-whitespace {
  white-space: normal !important;
  word-break: break-word;
}
</style>
